import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import {
  Code2, ChevronRight, Sun, Moon, Play, Sparkles, GraduationCap, Beaker, Gamepad2,
  Brain, Trophy, Calendar, Rocket, Bot, Cpu, Bug, Rabbit, HeartPulse, Star, Flame,
  BookOpen, Zap, Target, TrendingUp,
} from 'lucide-react';
import { PriyaCaricature, AaravCaricature, TeachersCaricature, RituCaricature } from './LandingPersonas';

// AI-generated painted-cartoon caricatures live on the backend.  We show
// them as the primary visual; the hand-drawn SVG stays as a graceful
// fallback if the API call fails.
const PERSONA_IMG = (name) => `${process.env.REACT_APP_BACKEND_URL}/api/personas/${name}.png`;

/* ================= Data ================= */
const competitiveExams = [
  { name: 'JEE Main',  color: 'from-blue-500 to-indigo-600',     icon: Trophy },
  { name: 'JEE Adv',   color: 'from-indigo-500 to-purple-600',   icon: Trophy },
  { name: 'NEET UG',   color: 'from-rose-500 to-red-600',        icon: HeartPulse },
  { name: 'BITSAT',    color: 'from-cyan-500 to-blue-500',       icon: Zap },
  { name: 'CUET',      color: 'from-emerald-500 to-green-600',   icon: BookOpen },
  { name: 'UPSC CSE',  color: 'from-amber-500 to-orange-600',    icon: Star },
  { name: 'CLAT',      color: 'from-violet-500 to-purple-600',   icon: BookOpen },
  { name: 'NDA',       color: 'from-slate-600 to-slate-800',     icon: Target },
  { name: 'GATE',      color: 'from-teal-500 to-cyan-600',       icon: Cpu },
  { name: 'CAT',       color: 'from-pink-500 to-rose-600',       icon: TrendingUp },
  { name: 'SSC CGL',   color: 'from-orange-500 to-red-500',      icon: Flame },
  { name: 'Bank PO',   color: 'from-yellow-500 to-amber-600',    icon: Target },
];

const dissections = [
  { name: 'Frog',       icon: Bug,       color: 'from-emerald-500 to-green-600', desc: 'Layer-by-layer anatomy — heart, lungs, muscles.' },
  { name: 'Rat',        icon: Rabbit,    color: 'from-slate-500 to-slate-700',   desc: 'Peel skin, expose organs, learn mammalian systems.' },
  { name: 'Human Body', icon: HeartPulse, color: 'from-rose-500 to-red-600',      desc: 'Skeleton → muscles → organs → circulation.' },
];

const gamifiedModules = [
  { name: 'Gamified Coding',   icon: Gamepad2, color: 'from-fuchsia-500 to-pink-600',   desc: 'Learn Python by fighting bosses & solving puzzles.' },
  { name: 'Drone Tech',        icon: Rocket,   color: 'from-cyan-500 to-blue-600',      desc: 'Design, code, and fly a virtual quadcopter.' },
  { name: 'Robotics',          icon: Bot,      color: 'from-indigo-500 to-violet-600',  desc: 'Build & program a robot arm — pick, place, sort.' },
  { name: 'Space Research',    icon: Rocket,   color: 'from-purple-600 to-indigo-800',  desc: 'Launch a rocket, orbit satellites, calc trajectories.' },
  { name: 'Game-Based Learn',  icon: Gamepad2, color: 'from-amber-500 to-orange-600',   desc: 'Learn algebra inside Angry Birds, Mario, Tetris.' },
];

/* ================= Small components ================= */
const AnimatedBlob = ({ className, delay = '0s' }) => (
  <div
    className={`absolute rounded-full blur-3xl opacity-40 pointer-events-none ${className}`}
    style={{ animation: `blob 14s ease-in-out infinite`, animationDelay: delay }}
  />
);

const ExamPill = ({ e, i }) => {
  const Icon = e.icon;
  return (
    <div
      className="group flex items-center gap-2 pl-2 pr-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/15 text-white shadow-lg hover:-translate-y-0.5 transition-transform"
      style={{ animation: `pop 0.6s ease-out both`, animationDelay: `${i * 0.05}s` }}
      data-testid={`landing-exam-${e.name.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <span className={`w-6 h-6 rounded-full bg-gradient-to-br ${e.color} flex items-center justify-center`}>
        <Icon className="w-3.5 h-3.5 text-white" />
      </span>
      <span className="text-sm font-semibold">{e.name}</span>
    </div>
  );
};

/* Live-looking mock-test engine card */
const MockEnginePreview = () => {
  const [pct, setPct] = useState(23);
  const [q, setQ] = useState(7);
  useEffect(() => {
    const iv = setInterval(() => {
      setPct(p => (p >= 92 ? 23 : p + 3));
      setQ(v => (v >= 30 ? 7 : v + 1));
    }, 900);
    return () => clearInterval(iv);
  }, []);
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/70 backdrop-blur p-5 shadow-2xl text-white" data-testid="landing-mock-engine">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-400 font-bold">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          Live Mock Test Engine
        </div>
        <div className="text-xs text-white/60 font-mono">01:24:37</div>
      </div>
      <div className="text-sm font-medium mb-1">Q{q}. In a Young&apos;s double slit experiment, fringe width is inversely proportional to…</div>
      <div className="mt-3 space-y-2 text-xs">
        {['(a) slit separation d', '(b) wavelength λ', '(c) screen distance D', '(d) intensity I₀'].map((o, i) => (
          <div key={i} className={`px-3 py-2 rounded-lg border ${i === 0 ? 'border-emerald-400/60 bg-emerald-400/10' : 'border-white/10 bg-white/5'}`}>
            {o}
          </div>
        ))}
      </div>
      <div className="mt-4">
        <div className="flex justify-between text-[11px] text-white/60 mb-1">
          <span>Progress</span><span>{pct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500 transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
};

/* Mini study-plan calendar */
const CalendarPreview = () => {
  const days = Array.from({ length: 28 }, (_, i) => i + 1);
  const marked = { 3: 'p', 4: 'e', 8: 'p', 9: 'p', 12: 'e', 14: 'e', 17: 'p', 21: 'e', 22: 'p', 25: 'p', 27: 'e' };
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/70 backdrop-blur p-5 shadow-2xl text-white" data-testid="landing-calendar">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-bold">Study Planner — this month</span>
        </div>
        <span className="text-[10px] text-white/50">P = practice · E = exam</span>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => (
          <div key={d} className="text-[10px] text-white/40 text-center">{d}</div>
        ))}
        {days.map(d => (
          <div key={d}
               className={`h-8 rounded-md flex items-center justify-center text-[10px] font-semibold
                          ${marked[d] === 'e' ? 'bg-rose-500/25 text-rose-200 ring-1 ring-rose-400/40' :
                            marked[d] === 'p' ? 'bg-emerald-500/25 text-emerald-200 ring-1 ring-emerald-400/40' :
                            'bg-white/5 text-white/40'}`}>
            {d}
          </div>
        ))}
      </div>
    </div>
  );
};

/* Concept clearance strip — mini narrator + moving object */
const ConceptClearancePreview = () => (
  <div className="rounded-2xl border border-white/10 bg-slate-900/70 backdrop-blur p-4 shadow-2xl text-white overflow-hidden" data-testid="landing-concept">
    <div className="flex items-center gap-2 mb-2">
      <Brain className="w-4 h-4 text-fuchsia-400" />
      <span className="text-sm font-bold">Concept Simplification</span>
      <span className="ml-auto text-[10px] text-white/50">Newton&apos;s Third Law</span>
    </div>
    <div className="relative h-28 bg-[linear-gradient(180deg,#f8fafc_0%,#e2e8f0_100%)] rounded-lg overflow-hidden">
      <svg viewBox="0 0 400 120" className="w-full h-full">
        <line x1="10" y1="95" x2="390" y2="95" stroke="#334155" strokeWidth="2" />
        <rect y="65" width="60" height="26" fill="#f59e0b" stroke="#78350f" strokeWidth="1.5" rx="3">
          <animate attributeName="x" values="80;280;80" dur="4s" repeatCount="indefinite" />
        </rect>
        <circle r="14" fill="#ef4444" stroke="#7f1d1d">
          <animate attributeName="cx" values="20;380;20" dur="4s" repeatCount="indefinite" />
          <animate attributeName="cy" values="80;80;80" dur="4s" repeatCount="indefinite" />
        </circle>
        <text x="180" y="30" fontSize="14" fontWeight="700" fill="#0f172a">F = m · a</text>
      </svg>
    </div>
  </div>
);

/* Virtual lab preview — molecules bouncing */
const LabPreview = () => (
  <div className="rounded-2xl border border-white/10 bg-slate-900/70 backdrop-blur p-4 shadow-2xl text-white overflow-hidden" data-testid="landing-lab">
    <div className="flex items-center gap-2 mb-2">
      <Beaker className="w-4 h-4 text-cyan-400" />
      <span className="text-sm font-bold">Visual Lab · Kinetic Molecules</span>
    </div>
    <div className="relative h-28 rounded-lg bg-gradient-to-b from-cyan-900/50 to-slate-900 overflow-hidden">
      <svg viewBox="0 0 400 120" className="w-full h-full">
        {Array.from({ length: 14 }).map((_, i) => (
          <circle key={i} r={5 + (i % 3)} fill={['#38bdf8', '#a78bfa', '#f472b6'][i % 3]}>
            <animate attributeName="cx"
              values={`${(i * 27) % 380 + 10};${(i * 43) % 380 + 10};${(i * 27) % 380 + 10}`}
              dur={`${2 + (i % 4)}s`} repeatCount="indefinite" />
            <animate attributeName="cy"
              values={`${(i * 17) % 100 + 10};${(i * 31) % 100 + 10};${(i * 17) % 100 + 10}`}
              dur={`${2 + (i % 4)}s`} repeatCount="indefinite" />
          </circle>
        ))}
      </svg>
    </div>
  </div>
);

/* ================= Page ================= */
export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-hidden">
      {/* Global CSS animations */}
      <style>{`
        @keyframes blob { 0%,100% { transform: translate(0,0) scale(1);} 33% { transform: translate(30px,-40px) scale(1.15);} 66% { transform: translate(-25px,25px) scale(0.9);} }
        @keyframes pop { from { opacity: 0; transform: translateY(8px) scale(0.9);} to { opacity: 1; transform: translateY(0) scale(1);} }
        @keyframes marquee { from { transform: translateX(0);} to { transform: translateX(-50%);} }
        @keyframes drift { from { transform: translateY(0);} to { transform: translateY(-8px);} }
        .marquee-track { animation: marquee 45s linear infinite; }
      `}</style>

      {/* Aurora background */}
      <div className="fixed inset-0 pointer-events-none">
        <AnimatedBlob className="w-[540px] h-[540px] bg-fuchsia-500 -top-40 -left-24" />
        <AnimatedBlob className="w-[480px] h-[480px] bg-cyan-500 top-1/3 -right-24" delay="3s" />
        <AnimatedBlob className="w-[520px] h-[520px] bg-indigo-500 bottom-0 left-1/3" delay="6s" />
        <div className="absolute inset-0"
             style={{
               backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.05) 1px, transparent 1px)',
               backgroundSize: '32px 32px',
             }} />
      </div>

      {/* NAV */}
      <nav className="relative z-20 border-b border-white/10 backdrop-blur bg-slate-950/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/30">
              <Code2 className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight">CognitiaIndia</span>
          </Link>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-btn" className="text-white hover:bg-white/10">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Link to="/login">
              <Button variant="ghost" data-testid="login-nav-btn" className="text-white hover:bg-white/10">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button data-testid="get-started-btn"
                      className="rounded-full px-6 bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-90 shadow-lg shadow-fuchsia-500/30">
                Get Started <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative z-10 pt-14 pb-6">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold text-cyan-200">
              <Sparkles className="w-3.5 h-3.5" />
              CBSE · ICSE · SSC · HSC · JEE · NEET · UPSC — one platform
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
              Learn like a{' '}
              <span className="bg-gradient-to-r from-fuchsia-400 via-pink-400 to-amber-300 bg-clip-text text-transparent">
                superhero
              </span>
              .<br />
              Practice like a pro. Ace every exam.
            </h1>
            <p className="text-white/70 max-w-xl text-lg">
              Board syllabus, competitive mock engines, animated concept clearance, virtual dissections,
              gamified coding, drones, robots and space missions — all under one glowing roof.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" data-testid="hero-cta-btn"
                        className="rounded-full px-7 h-12 bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-90 shadow-xl shadow-fuchsia-500/30">
                  Start free <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" data-testid="hero-signin-btn"
                        className="rounded-full px-7 h-12 bg-white/5 border-white/20 text-white hover:bg-white/10">
                  <Play className="w-4 h-4 mr-2" /> Watch a demo
                </Button>
              </Link>
            </div>
            <div className="flex gap-8 pt-2">
              {[
                ['32+', 'Courses', 'text-fuchsia-300'],
                ['54+', 'Competitive exams', 'text-cyan-300'],
                ['80+', 'Interactive labs', 'text-amber-300'],
              ].map(([n, l, c]) => (
                <div key={l}>
                  <div className={`text-3xl font-black ${c}`}>{n}</div>
                  <div className="text-xs uppercase tracking-wider text-white/50">{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative grid grid-cols-2 gap-4">
            <div className="col-span-2"><MockEnginePreview /></div>
            <CalendarPreview />
            <ConceptClearancePreview />
            <div className="col-span-2"><LabPreview /></div>
          </div>
        </div>
      </section>

      {/* COMPETITIVE EXAM MARQUEE */}
      <section className="relative z-10 py-10 border-y border-white/5 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-6 mb-4">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-400" /> Every competitive exam, ready-to-drill
          </h2>
        </div>
        <div className="relative overflow-hidden">
          <div className="marquee-track flex gap-3 w-max">
            {[...competitiveExams, ...competitiveExams].map((e, i) => (
              <ExamPill key={i} e={e} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* VIRTUAL DISSECTIONS */}
      <section className="relative z-10 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-3xl font-black flex items-center gap-2">
                <HeartPulse className="w-7 h-7 text-rose-400" /> Virtual dissections
              </h2>
              <p className="text-white/60 mt-1">Zero mess. Full anatomy. Learn by peeling layers, not scalpels.</p>
            </div>
            <Link to="/labs/virtual-dissection" className="text-cyan-300 text-sm font-semibold hover:underline">
              Enter the lab →
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {dissections.map((d, i) => {
              const Icon = d.icon;
              return (
                <Link
                  key={d.name}
                  to="/labs/virtual-dissection"
                  data-testid={`landing-dissect-${d.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="group relative rounded-2xl border border-white/10 bg-slate-900/60 p-6 overflow-hidden hover:-translate-y-1 hover:border-white/25 transition-all"
                  style={{ animation: `pop 0.7s ease-out both`, animationDelay: `${i * 0.1}s` }}
                >
                  <div className={`absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl opacity-40 bg-gradient-to-br ${d.color}`} />
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${d.color} flex items-center justify-center shadow-lg mb-4`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-1">{d.name}</h3>
                  <p className="text-sm text-white/60">{d.desc}</p>
                  <div className="mt-4 flex items-center gap-1 text-xs text-cyan-300 opacity-0 group-hover:opacity-100 transition-opacity">
                    Start dissection <ChevronRight className="w-3 h-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* GAMIFIED MODULES */}
      <section className="relative z-10 py-16 bg-gradient-to-b from-transparent via-fuchsia-500/[0.04] to-transparent">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-8">
            <h2 className="text-3xl font-black flex items-center gap-2">
              <Gamepad2 className="w-7 h-7 text-fuchsia-400" /> Learn by playing
            </h2>
            <p className="text-white/60 mt-1">Gamified coding, drones, robots and space missions.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {gamifiedModules.map((g, i) => {
              const Icon = g.icon;
              return (
                <div
                  key={g.name}
                  data-testid={`landing-game-${g.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  className="group relative rounded-2xl border border-white/10 bg-slate-900/60 p-5 overflow-hidden hover:-translate-y-1 hover:border-white/25 transition-all"
                  style={{ animation: `pop 0.7s ease-out both`, animationDelay: `${i * 0.08}s` }}
                >
                  <div className={`absolute inset-x-0 -top-24 h-40 blur-3xl opacity-40 bg-gradient-to-b ${g.color}`} />
                  <div className={`relative w-12 h-12 rounded-xl bg-gradient-to-br ${g.color} flex items-center justify-center shadow-lg mb-3`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="relative font-bold text-base mb-1">{g.name}</h3>
                  <p className="relative text-xs text-white/60">{g.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* KEY-FEATURE GRID */}
      <section className="relative z-10 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-black">Everything you need to excel</h2>
            <p className="text-white/60 mt-2">From board exams to competitive entrances — one complete ecosystem</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: BookOpen,   title: 'Board-aligned courses',    desc: 'CBSE, ICSE, SSC & HSC — grade-specific content.',  color: 'from-blue-500 to-indigo-600' },
              { icon: Brain,      title: 'AI-powered exams',         desc: 'MCQs generated, graded and explained by AI.',       color: 'from-violet-500 to-purple-600' },
              { icon: Trophy,     title: '54+ competitive exams',    desc: 'JEE / NEET / UPSC / CLAT / GATE + practice packs.', color: 'from-amber-500 to-orange-600' },
              { icon: Beaker,     title: '80+ interactive labs',     desc: 'Physics, Chemistry, Math simulations on the browser.', color: 'from-rose-500 to-pink-600' },
              { icon: GraduationCap, title: 'AI teacher avatars',    desc: 'Diverse Indian narrators explain topics visually.',  color: 'from-cyan-500 to-blue-600' },
              { icon: Calendar,   title: 'Smart study planner',      desc: 'Weekly plan + streaks + parent share.',              color: 'from-emerald-500 to-teal-600' },
            ].map((f, i) => (
              <div key={i}
                   className="rounded-2xl border border-white/10 bg-slate-900/60 p-6 hover:-translate-y-1 hover:border-white/25 transition-all"
                   style={{ animation: `pop 0.6s ease-out both`, animationDelay: `${i * 0.05}s` }}>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-3 shadow-lg`}>
                  <f.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-1">{f.title}</h3>
                <p className="text-sm text-white/60">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================= */}
      {/* Phase 40.36 — Persona sections requested by client              */}
      {/* ============================================================= */}

      {/* GIRL STUDENT · EXAMS */}
      <section className="relative z-10 py-16 bg-gradient-to-br from-fuchsia-500/5 via-transparent to-cyan-500/5">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-10 items-center">
          <div style={{ perspective: '1200px' }}>
            <div className="relative w-full max-w-md mx-auto" style={{ transform: 'rotateY(-6deg)' }}>
              <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-fuchsia-500/30 via-pink-500/20 to-amber-400/20 blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden border-2 border-fuchsia-400/40 shadow-2xl aspect-[4/5] bg-gradient-to-br from-fuchsia-900 via-purple-900 to-pink-900" data-testid="persona-student-girl">
                <img
                  src={PERSONA_IMG('priya')}
                  alt="Priya - Class 12 exam ace"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/10 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="text-white font-black text-lg drop-shadow">Priya, 17</div>
                  <div className="text-fuchsia-200 text-xs font-semibold">CBSE Class 12 · JEE Mains aspirant</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {['CBSE', 'ICSE', 'JEE', 'NEET', 'UPSC'].map((t) => (
                      <span key={t} className="px-2 py-0.5 rounded-full bg-fuchsia-500/30 border border-fuchsia-300/40 text-fuchsia-100 text-[10px] font-bold">{t}</span>
                    ))}
                  </div>
                </div>
                <div className="absolute top-3 right-3 bg-emerald-500/95 text-white text-[10px] font-black px-2 py-1 rounded-full shadow-lg animate-pulse">
                  92% Mock Score
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/30 text-fuchsia-300 text-xs font-bold">
              <Trophy className="w-3.5 h-3.5" /> Meet Priya · Class 12 topper
            </div>
            <h2 className="mt-3 text-3xl lg:text-4xl font-black leading-tight">
              &ldquo;Every exam I&apos;ll ever face — <span className="text-fuchsia-400">already in here.</span>&rdquo;
            </h2>
            <p className="text-white/70 mt-4">
              CBSE and ICSE board papers, Maharashtra SSC / HSC, plus <b>JEE Main / Advanced, NEET UG,
              BITSAT, CUET, UPSC, CLAT, GATE, CAT, SSC CGL, Bank PO</b> — 54+ competitive exams live
              on one dashboard. AI generates unique papers per session, grades instantly, and shows
              the step-by-step solution for every question.
            </p>
            <Link to="/register" className="inline-flex items-center gap-1 mt-5 text-fuchsia-300 font-semibold hover:underline">
              Try a free mock <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* MALE STUDENT · CODING COURSES */}
      <section className="relative z-10 py-16 bg-gradient-to-br from-cyan-500/5 via-transparent to-emerald-500/5">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/15 border border-cyan-400/30 text-cyan-300 text-xs font-bold">
              <Code2 className="w-3.5 h-3.5" /> Meet Aarav · builds his own games
            </div>
            <h2 className="mt-3 text-3xl lg:text-4xl font-black leading-tight">
              &ldquo;I code, I ship — <span className="text-cyan-400">and then I show off.</span>&rdquo;
            </h2>
            <p className="text-white/70 mt-4">
              Real programming courses — <b>Python, JavaScript, C++, Java, Web Dev, React, Data
              Science, Machine Learning, Game Dev, Drones and Robotics</b>. Every course ships with
              an in-browser IDE, live run for 12+ languages, autosave history and a &ldquo;Gemu&rdquo;
              tutor bot that walks through every mission.
            </p>
            <Link to="/courses" className="inline-flex items-center gap-1 mt-5 text-cyan-300 font-semibold hover:underline">
              Browse courses <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="order-1 lg:order-2" style={{ perspective: '1200px' }}>
            <div className="relative w-full max-w-md mx-auto" style={{ transform: 'rotateY(6deg)' }}>
              <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-cyan-500/30 via-sky-500/20 to-emerald-400/20 blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden border-2 border-cyan-400/40 shadow-2xl aspect-[4/5] bg-gradient-to-br from-cyan-900 via-sky-900 to-blue-900" data-testid="persona-student-boy">
                <img
                  src={PERSONA_IMG('aarav')}
                  alt="Aarav - Class 10 coder"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/10 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="text-white font-black text-lg drop-shadow">Aarav, 15</div>
                  <div className="text-cyan-200 text-xs font-semibold">Class 10 · Ships his own games</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {['Python', 'JS', 'React', 'ML', 'Robotics'].map((t) => (
                      <span key={t} className="px-2 py-0.5 rounded-full bg-cyan-500/30 border border-cyan-300/40 text-cyan-100 text-[10px] font-bold">{t}</span>
                    ))}
                  </div>
                </div>
                <div className="absolute top-3 left-3 bg-amber-400/95 text-slate-900 text-[10px] font-black px-2 py-1 rounded-full shadow-lg">
                  47 Streak Days
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TEACHERS · MEGAPHONE ENDORSEMENT */}
      <section className="relative z-10 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-3xl border border-amber-400/25 bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-rose-500/10 p-8 lg:p-12 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-amber-400/10 blur-3xl" />
            <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 items-center">
              <div className="w-full max-w-lg mx-auto relative rounded-3xl overflow-hidden border-2 border-amber-400/40 shadow-2xl aspect-[6/5] bg-gradient-to-br from-amber-900 via-orange-900 to-rose-900" data-testid="persona-teachers">
                <img
                  src={PERSONA_IMG('teachers')}
                  alt="Cognitia teachers"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/5 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                  <div>
                    <div className="text-white font-black text-lg drop-shadow">Meera · Arjun · Anita</div>
                    <div className="text-amber-200 text-xs font-semibold">Physics · Math · Chemistry</div>
                  </div>
                  <span className="px-2 py-0.5 rounded-full bg-amber-400/95 text-slate-900 text-[10px] font-black">10 teachers</span>
                </div>
              </div>
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 text-xs font-bold">
                  <GraduationCap className="w-3.5 h-3.5" /> 240+ verified teachers agree
                </div>
                <div className="mt-4 text-4xl lg:text-5xl font-black leading-tight">
                  <span className="text-amber-300 text-6xl leading-none">&ldquo;</span>
                  <span className="bg-gradient-to-r from-amber-300 to-orange-300 bg-clip-text text-transparent">Only CognitiaIndia</span>
                  <span className="text-amber-300 text-6xl leading-none">&rdquo;</span>
                </div>
                <p className="text-white/80 mt-3 text-lg">
                  &mdash; the boards-aligned, exam-ready, code-teaching platform we actually
                  recommend to our students.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HAPPY PARENT · MEGAPHONE */}
      <section className="relative z-10 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-3xl border border-emerald-400/25 bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-cyan-500/10 p-8 lg:p-12 relative overflow-hidden">
            <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-emerald-400/10 blur-3xl" />
            <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-xs font-bold">
                  <HeartPulse className="w-3.5 h-3.5" /> Parents love it
                </div>
                <div className="mt-4 text-3xl lg:text-4xl font-black leading-tight">
                  <span className="text-emerald-300 text-5xl leading-none">&ldquo;</span>
                  <span className="bg-gradient-to-r from-emerald-300 to-cyan-300 bg-clip-text text-transparent">
                    CognitiaIndia makes our kids smarter to cope up with the changing world
                  </span>
                  <span className="text-emerald-300 text-5xl leading-none">&rdquo;</span>
                </div>
                <p className="text-white/80 mt-4 text-base">
                  Weekly plans, live progress, instant score reports. My daughter&apos;s syllabus and
                  competitive prep are finally on <em>one</em> screen — and she&apos;s coding too.
                </p>
                <p className="text-white/60 mt-2 text-sm">— Ritu S., Parent · Pune</p>
              </div>
              <div className="relative w-full max-w-md mx-auto">
                <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-emerald-500/30 via-teal-500/20 to-cyan-400/20 blur-2xl" />
                <div className="relative rounded-3xl overflow-hidden border-2 border-emerald-400/40 shadow-2xl aspect-[4/5] bg-gradient-to-br from-emerald-900 via-teal-900 to-green-900" data-testid="persona-parent">
                  <img
                    src={PERSONA_IMG('ritu')}
                    alt="Ritu - Cognitia parent"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/10 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className="text-white font-black text-lg drop-shadow">Ritu S., 42</div>
                    <div className="text-emerald-200 text-xs font-semibold">Mother of two · Pune</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {['Weekly Plan', 'Live Progress', 'Parent App'].map((t) => (
                        <span key={t} className="px-2 py-0.5 rounded-full bg-emerald-500/30 border border-emerald-300/40 text-emerald-100 text-[10px] font-bold">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="absolute top-3 right-3 bg-white/95 text-slate-900 text-[10px] font-black px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
                    ⭐ 4.9 · Parent rating
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ============================================================= */}
      {/* /Phase 40.36 persona sections                                    */}
      {/* ============================================================= */}

      {/* CTA */}
      <section className="relative z-10 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-3xl p-10 border border-white/10 bg-gradient-to-br from-fuchsia-600/20 via-indigo-600/10 to-cyan-500/20">
            <AnimatedBlob className="w-72 h-72 bg-fuchsia-500 -top-10 -left-10" />
            <AnimatedBlob className="w-72 h-72 bg-cyan-500 -bottom-10 -right-10" delay="4s" />
            <div className="relative text-center">
              <h2 className="text-3xl lg:text-4xl font-black">Ready to begin?</h2>
              <p className="text-white/70 mt-2 mb-6 max-w-xl mx-auto">
                Free forever core courses. No credit card. Ace boards, crack JEE/NEET, learn to code and build robots.
              </p>
              <Link to="/register">
                <Button size="lg" data-testid="cta-register-btn"
                        className="rounded-full px-8 h-12 bg-white text-slate-900 hover:bg-white/90 font-bold">
                  Create free account <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-8 text-center text-xs text-white/50">
        © 2026 CognitiaIndia — Learn brave, dream loud.
      </footer>
    </div>
  );
}
